import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';

const SupplierMetricsCards = ({ language, startDate, endDate }) => {
  const [metrics, setMetrics] = useState({
    totalSuppliers: 0,
    avgOrderFrequency: 0,
    avgResponseTime: 0,
    satisfactionRate: 0
  });
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Supplier Relationship Metrics',
      totalSuppliers: 'Active Suppliers',
      orderFrequency: 'Avg Order Frequency',
      responseTime: 'Avg Response Time',
      satisfaction: 'Satisfaction Rate',
      ordersPerMonth: 'orders/month',
      hours: 'hours'
    },
    zh: {
      title: '供应商关系指标',
      totalSuppliers: '活跃供应商',
      orderFrequency: '平均订单频率',
      responseTime: '平均响应时间',
      satisfaction: '满意度',
      ordersPerMonth: '订单/月',
      hours: '小时'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadMetrics();
  }, [startDate, endDate]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();

      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Calculate metrics
      const uniqueSuppliers = new Set(filtered?.map(o => o?.bean?.supplierId));
      const totalSuppliers = uniqueSuppliers?.size;
      
      const monthsDiff = Math.max(1, (endDate - startDate) / (1000 * 60 * 60 * 24 * 30));
      const avgOrderFrequency = (filtered?.length / monthsDiff)?.toFixed(1);
      
      // Mock response time and satisfaction (would come from actual data)
      const avgResponseTime = 24;
      const satisfactionRate = 92;

      setMetrics({
        totalSuppliers,
        avgOrderFrequency,
        avgResponseTime,
        satisfactionRate
      });
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricsData = [
    {
      id: 1,
      label: t?.totalSuppliers,
      value: metrics?.totalSuppliers,
      icon: 'Users',
      color: 'primary',
      suffix: ''
    },
    {
      id: 2,
      label: t?.orderFrequency,
      value: metrics?.avgOrderFrequency,
      icon: 'ShoppingCart',
      color: 'secondary',
      suffix: t?.ordersPerMonth
    },
    {
      id: 3,
      label: t?.responseTime,
      value: metrics?.avgResponseTime,
      icon: 'Clock',
      color: 'accent',
      suffix: t?.hours
    },
    {
      id: 4,
      label: t?.satisfaction,
      value: `${metrics?.satisfactionRate}%`,
      icon: 'Star',
      color: 'success',
      suffix: ''
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary',
      secondary: 'bg-secondary/10 text-secondary',
      accent: 'bg-accent/10 text-accent-foreground',
      success: 'bg-success/10 text-success'
    };
    return colors?.[color] || colors?.primary;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Users" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsData?.map((metric) => (
          <div key={metric?.id} className="bg-muted/30 rounded-lg p-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${getColorClasses(metric?.color)}`}>
              <Icon name={metric?.icon} size={20} />
            </div>
            <h4 className="text-2xl font-bold text-foreground mb-1">
              {metric?.value} {metric?.suffix && <span className="text-sm font-normal text-muted-foreground">{metric?.suffix}</span>}
            </h4>
            <p className="text-sm text-muted-foreground">{metric?.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SupplierMetricsCards;